import { computed, makeObservable, observable } from "mobx";
import moment from "moment";
import { ICommand } from "Application";
import { ViewModelBase } from "@shoothill/core";

import { WeeklyRotaDetailModel } from "./WeeklyRotaDetailModel";
import { AddStudentViewModel } from "./AddStudent/AddStudentViewModel";
import { ManagersModule } from "./Managers";
import { MentorAvailability } from "./Types";

export class WeeklyRotaDetailsViewModel extends ViewModelBase<WeeklyRotaDetailModel> {
    public managers!: ManagersModule;

    // addStudentViewModel is kept here (not in MobXManager) to avoid a circular
    // import: MobXManager → AddStudentViewModel → WeeklyRotaDetailsViewModel.
    public addStudentViewModel: AddStudentViewModel;

    constructor() {
        super(new WeeklyRotaDetailModel());
        this.addStudentViewModel = new AddStudentViewModel(this.model.addStudentModel);
        this.managers = new ManagersModule(this);

        makeObservable(this, {
            addStudentViewModel: observable,
            showAddStudentModal: computed,
            studentRotaSessionTime: computed,
            studentRotaAttendanceText: computed,
            isOverdueSession: computed,
            sessionReportButtonLabel: computed,
            sessionDateDisplay: computed,
            hasIncident: computed,
            incidentText: computed,
            sessionStatusColour: computed,
            showSessionReportButton: computed,
            sessionTimeDisplay: computed,
            showTransportDetails: computed,
            hasAbsence: computed,
            startDateForAbsenceRange: computed,
            hasStudentRota: computed,
            mentorAvailabilityList: computed,
            hasWeeklyRota: computed,
            hasNoWeeklyRota: computed,
            sessionLeftByDay: computed,
        });

        this.model.startDate = moment(new Date()).startOf("isoWeek").toDate();
        this.addStudentViewModel.initialize();
        this.managers.api.loadLeftStudentSession();
    }

    // ─── UIManager state — delegated ─────────────────────────────────────────

    public get isBusy(): boolean {
        return this.managers.ui.isBusy;
    }
    public get isProcessing(): boolean {
        return this.managers.ui.isProcessing;
    }

    // ─── MobX state — delegated to MobXManager ───────────────────────────────

    public get weeklyRotaModalOpen(): boolean {
        return this.managers.mobx.weeklyRotaModalOpen;
    }
    public get mentorAbsenceModalOpen(): boolean {
        return this.managers.mobx.mentorAbsenceModalOpen;
    }

    public get sessionLeftModalOpen(): boolean {
        return this.managers.mobx.sessionLeftModalOpen;
    }

    public get deletingStudentSessionId(): Guid | null {
        return this.managers.mobx.deletingStudentSessionId;
    }
    public get rotaEditable(): boolean {
        return this.managers.mobx.rotaEditable;
    }
    public get mentorStudentAdded(): boolean {
        return this.managers.mobx.mentorStudentAdded;
    }
    public get successfullyGetWeeklyRota(): boolean {
        return this.managers.mobx.successfullyGetWeeklyRota;
    }
    public get sessionKey(): string {
        return this.managers.mobx.sessionKey;
    }
    public get openAddStudentModal(): boolean {
        return this.managers.mobx.openAddStudentModal;
    }
    public get showAddStudentModal(): boolean {
        return this.managers.mobx.showAddStudentModal;
    }
    public get openOverTimeConfirmationModal(): boolean {
        return this.managers.mobx.openOverTimeConfirmationModal;
    }
    public get openStudentDetailModal(): boolean {
        return this.managers.mobx.openStudentDetailModal;
    }
    public get resetRotaConfirmationOpen(): boolean {
        return this.managers.mobx.resetRotaConfirmationOpen;
    }

    // ─── Commands — delegated to CommandsManager ─────────────────────────────

    public get toggleWeeklyRotaModalCommand(): ICommand {
        return this.managers.commands.toggleWeeklyRotaModalCommand;
    }
    public get showMentorAbsenceModalCommand(): ICommand {
        return this.managers.commands.showMentorAbsenceModalCommand;
    }

    public get showSessionLeftModalCommand(): ICommand {
        return this.managers.commands.showSessionLeftModalCommand;
    }

    public get updateRotaStartDateCommand(): ICommand {
        return this.managers.commands.updateRotaStartDateCommand;
    }
    public get updateRotaDateRangeCommand(): ICommand {
        return this.managers.commands.updateRotaDateRangeCommand;
    }
    public get updateNextDateCommand(): ICommand {
        return this.managers.commands.updateNextDateCommand;
    }
    public get updatePreviousDateCommand(): ICommand {
        return this.managers.commands.updatePreviousDateCommand;
    }
    public get showEditableScheduleCommand(): ICommand {
        return this.managers.commands.showEditableScheduleCommand;
    }

    public get showResetRotaConfirmationCommand(): ICommand {
        return this.managers.commands.showResetRotaConfirmationCommand;
    }
    public get closeResetRotaConfirmationCommand(): ICommand {
        return this.managers.commands.closeResetRotaConfirmationCommand;
    }
    public get resetRotaCommand(): ICommand {
        return this.managers.commands.resetRotaCommand;
    }
    public get navigateToDetailCommand(): ICommand {
        return this.managers.commands.navigateToDetailCommand;
    }
    public get navigateToSessionDetailCommand(): ICommand {
        return this.managers.commands.navigateToSessionDetailCommand;
    }
    public get openAddStudentModalCommand(): ICommand {
        return this.managers.commands.openAddStudentModalCommand;
    }
    public get openOverTimeConfirmationModalCommand(): ICommand {
        return this.managers.commands.openOverTimeConfirmationModalCommand;
    }
    public get openStudentDetailModalCommand(): ICommand {
        return this.managers.commands.openStudentDetailModalCommand;
    }
    public get deleteStudentMentorCommand(): ICommand {
        return this.managers.commands.deleteStudentMentorCommand;
    }
    public get removeDetailsCommand(): ICommand {
        return this.managers.commands.removeDetailsCommand;
    }
    public get deleteMentorAbsenceForSlotCommand(): ICommand {
        return this.managers.commands.deleteMentorAbsenceForSlotCommand;
    }
    public get closeAddStudentModalCommand(): ICommand {
        return this.managers.commands.closeAddStudentModalCommand;
    }
    public get closeOverTimeConfirmationModalCommand(): ICommand {
        return this.managers.commands.closeOverTimeConfirmationModalCommand;
    }
    public get closeStudentDetailModalCommand(): ICommand {
        return this.managers.commands.closeStudentDetailModalCommand;
    }
    public get openAddStudentAfterOverTimeCommand(): ICommand {
        return this.managers.commands.openAddStudentAfterOverTimeCommand;
    }

    // ─── API methods — delegated to APIManager ────────────────────────────────
    // Kept as async methods (not commands) because some are called from child ViewModels
    // and external components that expect Promise<void>.

    public async getRotaByDateAsync(loadLeftStudentSessions: boolean = false): Promise<void> {
        return this.managers.api.getRotaByDateAsync(loadLeftStudentSessions);
    }

    public async loadLeftStudentSession(): Promise<void> {
        return this.managers.api.loadLeftStudentSession();
    }

    // ─── Domain computeds — delegated to DomainManager ───────────────────────

    /** Resolved start/end display times for the session detail modal. */
    public get studentRotaSessionTime(): { start: string; end: string } {
        return this.managers.domain.getStudentSessionTime(
            this.model.studentRota.sessionDate,
            this.model.studentRota.studentId,
            this.model.studentRota.studentName,
            this.model.studentList as any[],
            this.model.studentRota.sessionStartTime,
            this.model.studentRota.sessionEndTime,
        );
    }

    /** Attendance display text for the session detail modal. */
    public get studentRotaAttendanceText(): string {
        return this.managers.domain.attendanceText(this.model.studentRota.didAttend);
    }

    /** True when the current session status is "Overdue". */
    public get isOverdueSession(): boolean {
        return this.managers.domain.isOverdueSession(this.model.studentRota.sessionStatusName);
    }

    /** Label for the session report action button. */
    public get sessionReportButtonLabel(): string {
        return this.managers.domain.sessionReportButtonLabel(this.model.studentRota.didAttend);
    }

    /** Formatted session date string for display in the detail modal. */
    public get sessionDateDisplay(): string {
        return moment(this.model.studentRota.sessionDate).format("DD/MM/yyyy");
    }

    /** True when the session has a reported incident. */
    public get hasIncident(): boolean {
        return this.model.studentRota.reportIncident;
    }

    /** Incident display text for the detail modal. */
    public get incidentText(): string {
        return this.model.studentRota.reportIncident ? "(INCIDENT REPORTED)" : "";
    }

    /** Session status colour resolved via domain manager. */
    public get sessionStatusColour(): string {
        return this.managers.domain.statusTextColour(this.model.studentRota.sessionStatusName);
    }

    /** True when the session report button should be visible. */
    public get showSessionReportButton(): boolean {
        return this.model.studentRota.sessionStatusName === "Submitted";
    }

    /** Formatted session time range for display (e.g. "09:00-10:00"). */
    public get sessionTimeDisplay(): string {
        const { start, end } = this.studentRotaSessionTime;
        return `${start}-${end}`;
    }

    /** True when transport details should be shown (i.e. session is not overdue). */
    public get showTransportDetails(): boolean {
        return !this.isOverdueSession;
    }

    /** True when the student did not attend the session. */
    public get hasAbsence(): boolean {
        return this.model.studentRota.didAttend === false;
    }

    /** Formatted start date for use as the initial date in absence date pickers. */
    public get startDateForAbsenceRange(): string {
        return moment(this.model.startDate).format("DD-MM-YYYY");
    }

    /** True when a studentRota record is loaded. */
    public get hasStudentRota(): boolean {
        return !!this.model.studentRota;
    }

    /** Typed list of mentor availability records for the schedule grid. */
    public get mentorAvailabilityList(): MentorAvailability[] {
        return this.model.mentorAvailability as MentorAvailability[];
    }

    public get hasWeeklyRota(): boolean {
        return !!this.model.rotaId;
    }

    public get hasNoWeeklyRota(): boolean {
        return !this.model.rotaId && !this.managers.ui.isLoading;
    }

    /** Sessions left to assign, organised by day and AM/PM slot. */
    public get sessionLeftByDay(): { day: string; am: string[]; pm: string[] }[] {
        const days = [
            { day: "Monday", key: "Mon" },
            { day: "Tuesday", key: "Tues" },
            { day: "Wednesday", key: "Weds" },
            { day: "Thursday", key: "Thur" },
            { day: "Friday", key: "Fri" },
        ];
        return days
            .map(({ day, key }) => {
                const sessions = this.model.leftStudentSession as any[];
                const forSlot = (isAm: boolean) =>
                    sessions
                        .filter((std) => {
                            const s = std[`startTime${key}`];
                            const e = std[`endTime${key}`];
                            if (!s || !e || (s === "00:00:00" && e === "00:00:00")) return false;
                            const sh = parseInt(s.split(":")[0]);
                            const eh = parseInt(e.split(":")[0]);
                            return isAm ? sh >= 9 && sh <= 12 && eh >= 9 && eh <= 12 : sh < 9 || eh < 9 || eh > 12;
                        })
                        .map((std) => std.studentName as string);
                return { day, am: forSlot(true), pm: forSlot(false) };
            })
            .filter(({ am, pm }) => am.length > 0 || pm.length > 0);
    }
}
